import React, { useEffect, useState } from 'react';
import { opensInBrowser } from '../../utils/attachmentPreview';
import type { InboxEmailDetail, ReclassifyCategory } from '../../lib/inboxApi';
import {
  classifyInboxEmail,
  deleteInboxEmail,
  inboxReclassifyErrorMessage,
  updateInboxEmailCategory,
} from '../../lib/inboxApi';
import {
  RECLASSIFY_OPTIONS,
  reclassifyCategoriesOf,
  reclassifyCategoryLabel,
  reclassifyOutcomeMessage,
} from './reclassify';
import { Modal } from '../../components/Modal';
import { AttachmentPreviewModal } from '../../components/AttachmentPreviewModal';
import { UntrustedHtmlFrame } from '../../components/UntrustedHtmlFrame';
import { formatDateTime as fmtDateTime } from "../../utils/dateFormat";
import { ThreadTimeline } from './ThreadTimeline';
import { ReplyComposeModal } from './ReplyComposeModal';
import { ConfirmedOrderHoldNotice } from '../confirmedOrders/ConfirmedOrderHoldNotice';

interface Props {
  email?: InboxEmailDetail | null;
  /**
   * The conversation the open message belongs to, from the selected list row. Both optional:
   * the pane still renders one message correctly without them, which is what happens for mail
   * ingested before the threading migration ran.
   */
  threadId?: string;
  messageCount?: number;
  /**
   * The conversation's categories, from the selected list row. What Reclassify moves is the
   * conversation, and the open message may be our own reply, which carries no category at all.
   */
  threadInboxTypes?: string[];
  onEmailUpdated?: (email: InboxEmailDetail) => void;
  onEmailDeleted?: () => void;
}

const formatDateTime = (value?: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return fmtDateTime(d);
};

function getApiBaseUrl(): string {
  const w = window as any;
  const injected = typeof w.__API_BASE_URL__ === 'string' ? w.__API_BASE_URL__.trim() : '';
  if (injected) return injected.replace(/\/+$/, '');

  const origin = window.location.origin;
  const isLocal3000 = /localhost:3000$/i.test(origin) || /127\.0\.0\.1:3000$/i.test(origin);
  if (isLocal3000) return 'http://localhost:3001';
  return origin.replace(/\/+$/, '');
}

function buildAbsoluteFileUrl(fileUrl: string): string {
  const raw = String(fileUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = getApiBaseUrl();
  const pathClean = raw.startsWith('/') ? raw : `/${raw}`;
  return `${base}${pathClean}`;
}


// Prefer GCS objectPath when available; fallback to legacy fileUrl/storagePath.
function getAttachmentUrl(att: any): string {
  if (!att) return '';

  const fileUrl = (att?.fileUrl as string | undefined) || '';
  if (fileUrl) return buildAbsoluteFileUrl(fileUrl);

  const objectPath =
    String(att?.storage?.objectPath || att?.objectPath || '').trim() ||
    (() => {
      const gsUri = String(att?.storage?.gsUri || att?.gsUri || '').trim();
      if (!gsUri.startsWith('gs://')) return '';
      const rest = gsUri.slice('gs://'.length);
      const idx = rest.indexOf('/');
      if (idx === -1) return '';
      return rest.slice(idx + 1);
    })();

  if (objectPath) {
    const cleaned = objectPath.replace(/^\/+/, '');
    return buildAbsoluteFileUrl(`/files/${cleaned}`);
  }

  const sp = String(att?.storagePath || '').trim();
  if (sp) {
    const cleaned = sp.replace(/^\/+/, '');
    return buildAbsoluteFileUrl(`/files/${cleaned}`);
  }

  return '';
}

export const EmailDetail: React.FC<Props> = ({
  email,
  threadId,
  messageCount,
  threadInboxTypes,
  onEmailUpdated,
  onEmailDeleted,
}) => {
  const [viewMode, setViewMode] = useState<'html' | 'text'>('html');
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [reclassifySaving, setReclassifySaving] = useState(false);
  // '' until the user picks one: a move relabels every message the client sent and can build or
  // park RFQs, so Save never fires on a preselected default.
  const [selectedCategory, setSelectedCategory] = useState<ReclassifyCategory | ''>('');
  const [reclassifyError, setReclassifyError] = useState<string | null>(null);
  const [reclassifyNotice, setReclassifyNotice] = useState<string | null>(null);
  const [aiClassifying, setAiClassifying] = useState(false);
  const [previewAtt, setPreviewAtt] = useState<{ url: string; filename?: string; mimeType?: string } | null>(null);

  // A notice belongs to the conversation it was about; opening another email clears it.
  useEffect(() => {
    setReclassifyNotice(null);
  }, [email?._id]);

  const emailHtml: string = email?.bodyHtml || email?.rawBodyHtml || '';

  if (!email) {
    return (
      <div className="flex h-full items-center justify-center bg-[#f7faf8]">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <span className="text-lg text-slate-400">✉️</span>
          </div>
          <div className="text-sm font-semibold text-slate-700">Select an item to read</div>
          <div className="mt-1 text-xs text-slate-400">Nothing is selected.</div>
        </div>
      </div>
    );
  }

  const emailSubject = String(email.subject || '').trim() || '(No subject)';
  const emailText: string = email.bodyText || email.rawBodyText || '';

  const attachments = email.attachments || [];
  const aiConfidence =
    typeof email.aiInboxConfidence === 'number'
      ? Math.max(0, Math.min(100, Math.round(email.aiInboxConfidence * 100)))
      : null;

  // The conversation's categories when the pane knows them (the list row), else the open message's.
  const currentCategories = reclassifyCategoriesOf(
    threadInboxTypes && threadInboxTypes.length ? threadInboxTypes : email.inboxTypes,
  );

  const openReclassify = () => {
    setSelectedCategory('');
    setReclassifyError(null);
    setReclassifyOpen(true);
  };

  const handleSaveReclassify = async () => {
    if (!email || !selectedCategory) return;
    setReclassifySaving(true);
    setReclassifyError(null);
    try {
      // The row's message id, which may be our own reply: the server resolves the conversation
      // and moves every message the client sent in it.
      const updated = await updateInboxEmailCategory(email._id, selectedCategory);
      onEmailUpdated?.(updated);
      setReclassifyNotice(reclassifyOutcomeMessage(updated));
      setReclassifyOpen(false);
    } catch (e: unknown) {
      setReclassifyError(inboxReclassifyErrorMessage(e));
      // The move itself was saved; only building the RFQ failed. Refresh the list and badges so
      // the conversation shows where it now is.
      if ((e as { code?: unknown } | null)?.code === 'INBOX_RFQ_NOT_CREATED') onEmailUpdated?.(email);
    } finally {
      setReclassifySaving(false);
    }
  };

  const handleDeleteEmail = async () => {
    if (!email) return;
    const ok = window.confirm('Delete this email? This action cannot be undone.');
    if (!ok) return;
    try {
      await deleteInboxEmail(email._id);
      onEmailDeleted?.();
    } catch (e: any) {
      window.alert(e?.message || 'Failed to delete email');
    }
  };

  const handleAiClassify = async () => {
    if (!email) return;
    setAiClassifying(true);
    try {
      const updated = await classifyInboxEmail(email._id);
      // Open on our own reply, the server classifies the client's latest email and answers with
      // THAT one: refresh, but keep the pane on the message being read.
      onEmailUpdated?.(String(updated._id) === String(email._id) ? updated : email);
      // A suggestion for the menu, not a move: Save still has to be pressed.
      setSelectedCategory(reclassifyCategoriesOf(updated.inboxTypes)[0] || '');
    } catch (e: any) {
      window.alert(e?.message || 'Failed to classify email');
    } finally {
      setAiClassifying(false);
    }
  };

  return (
    <div className="h-full bg-white overflow-y-auto p-6">
      <div className="mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div
              className="text-sm font-semibold text-slate-800 truncate"
              title={emailSubject}
              aria-label={emailSubject}
            >
              {emailSubject}
            </div>

            <div className="mt-1 text-xs text-slate-500 space-x-1">
              {email.from && <span>{email.from}</span>}
              {email.receivedAt && (
                <>
                  <span>•</span>
                  <span>{formatDateTime(email.receivedAt)}</span>
                </>
              )}
            </div>

            {email.to && (
              <div className="mt-1 text-xs text-slate-500">
                <span className="font-semibold">To:</span> <span>{email.to}</span>
                {email.cc && (
                  <>
                    <span className="mx-1">|</span>
                    <span className="font-semibold">CC:</span> <span>{email.cc}</span>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {aiConfidence != null && (
              <span
                className={`px-2 py-1 rounded-full text-xs font-semibold border ${
                  email.aiInboxLowConfidence
                    ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : aiConfidence >= 80
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-cyan-50 text-cyan-700 border-cyan-200'
                }`}
                title={email.aiInboxLowConfidence ? 'Low confidence — please review' : 'AI classification confidence'}
              >
                {email.aiInboxLowConfidence ? '⚠ Review' : 'AI'} {aiConfidence}%
              </span>
            )}
            <button
              onClick={handleDeleteEmail}
              className="px-3 py-1.5 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-xs font-semibold hover:bg-rose-100"
            >
              Delete
            </button>
            <button
              onClick={openReclassify}
              className="px-3 py-1.5 rounded-md border border-cyan-200 bg-cyan-50 text-cyan-700 text-xs font-semibold hover:bg-cyan-100"
            >
              Reclassify
            </button>
            <button
              onClick={() => setReplyOpen(true)}
              className="px-3 py-1.5 rounded-md border border-sky-200 bg-sky-50 text-sky-700 text-xs font-semibold hover:bg-sky-100"
            >
              Reply
            </button>
          </div>
        </div>
        {reclassifyNotice && (
          <div role="status" className="mt-2 rounded border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs text-cyan-800">
            {reclassifyNotice}
          </div>
        )}
        {email.aiInboxReasoning && (
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-600">
            <span className="font-semibold text-slate-500">AI reasoning:</span> {email.aiInboxReasoning}
          </div>
        )}
      </div>

      {/* D45.7: a confirmation that waits for a person, with its Confirm order button. */}
      <ConfirmedOrderHoldNotice emailId={email._id} hold={email.confirmedOrderHold} />

      <ThreadTimeline threadId={threadId} messageCount={messageCount} currentMessageId={email._id} />

      {attachments.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center mb-2 gap-1 text-xs font-semibold text-slate-700">
            <span className="text-sm">📎</span>
            <span>Attachments</span>
          </div>
          <div className="border border-slate-200 rounded bg-[#f7faf8] px-3 py-2 space-y-1">
            {attachments.map((att: any, idx: number) => {
              const absUrl = getAttachmentUrl(att);
              const inline = opensInBrowser(att?.filename, att?.mimeType);

              return (
                <div
                  key={`${att.filename}-${idx}`}
                  className="flex items-center justify-between text-xs text-slate-700"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs flex-shrink-0">•</span>
                    <span className="truncate">{att.filename}</span>
                  </div>

                  <div className="ml-2 flex items-center gap-2 text-xs text-slate-500 flex-shrink-0">
                    {att.mimeType && <span>{att.mimeType}</span>}
                    {typeof att.size === 'number' && <span>{(att.size / 1024).toFixed(1)} KB</span>}

                    {absUrl && (
                      <button
                        type="button"
                        onClick={() => setPreviewAtt({ url: absUrl, filename: att?.filename, mimeType: att?.mimeType })}
                        className="text-primary-500 font-semibold hover:underline whitespace-nowrap"
                      >
                        View
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-semibold text-slate-600">Email body</div>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setViewMode('html')}
              className={`px-2 py-0.5 rounded border text-xs font-semibold ${
                viewMode === 'html'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              HTML
            </button>
            <button
              type="button"
              onClick={() => setViewMode('text')}
              className={`px-2 py-0.5 rounded border text-xs font-semibold ${
                viewMode === 'text'
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              Text
            </button>
          </div>
        </div>

        {viewMode === 'html' && emailHtml ? (
          <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 leading-relaxed overflow-x-auto">
            <UntrustedHtmlFrame html={emailHtml} title="Email message body" />
          </div>
        ) : (
          <div className="rounded border border-slate-200 bg-[#fdfdfd] px-3 py-2 text-xs text-slate-700 whitespace-pre-line leading-relaxed">
            {emailText || 'No email body available.'}
          </div>
        )}
      </div>

      <Modal isOpen={reclassifyOpen} onClose={() => setReclassifyOpen(false)} title="Reclassify conversation" maxWidthClass="max-w-md">
        <div className="space-y-4">
          <div className="text-xs text-slate-600">
            Currently in:{' '}
            <span className="font-semibold">
              {currentCategories.length ? currentCategories.map(reclassifyCategoryLabel).join(', ') : 'no category'}
            </span>
          </div>
          <div>
            <label htmlFor="reclassify-category" className="block text-xs font-semibold text-slate-700 mb-1">
              Move the conversation to
            </label>
            <select
              id="reclassify-category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as ReclassifyCategory | '')}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="" disabled>
                Choose a category
              </option>
              {RECLASSIFY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Every email the client sent in this conversation moves with it.
            </p>
          </div>
          {reclassifyError && (
            <div className="text-xs text-rose-600" role="alert">
              {reclassifyError}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={handleAiClassify}
              disabled={aiClassifying}
              className="px-3 py-2 rounded-md border border-cyan-200 text-xs font-semibold text-cyan-700 hover:bg-cyan-50 disabled:opacity-60"
            >
              {aiClassifying ? 'Classifying...' : 'Classify with AI'}
            </button>
            <button
              onClick={() => setReclassifyOpen(false)}
              className="px-3 py-2 rounded-md border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveReclassify}
              disabled={reclassifySaving || !selectedCategory}
              className="px-3 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-60"
            >
              {reclassifySaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      <ReplyComposeModal
        isOpen={replyOpen}
        onClose={() => setReplyOpen(false)}
        email={email}
        onSent={() => {
          // The SAME refresh path Reclassify uses. The original message is handed back, not
          // the outbound reply: onEmailUpdated replaces what the detail pane is showing, and
          // swapping the user onto the mail they just sent is not what "refresh" means here.
          // The reply reaches the UI through the thread/list reload that handler performs.
          onEmailUpdated?.(email);
        }}
      />

      {previewAtt && (
        <AttachmentPreviewModal
          url={previewAtt.url}
          filename={previewAtt.filename}
          mimeType={previewAtt.mimeType}
          onClose={() => setPreviewAtt(null)}
        />
      )}
    </div>
  );
};
